<template>
	<div class="product-import">
		<div class="product-import__head">
			<h1>GPT импорт товаров</h1>
			<p>Один товар за раз. GPT разберет данные, подберет справочники и создаст товар, оффер, характеристики и связи.</p>
		</div>

		<div class="product-import__grid">
			<div class="product-import__main">
				<label class="product-import__label">Сырые данные товара</label>
				<textarea
					v-model="rawProduct"
					class="product-import__textarea el-inp"
					placeholder='Можно вставить JSON или простой текст с описанием товара'
				></textarea>

				<div v-if="hintDismissed" class="product-import__options">
					<label class="product-import__checkbox">
						<input type="checkbox" v-model="allowNewDictionaries">
						<span>Создавать новые категории, бренды и характеристики</span>
					</label>
				</div>

				<div class="product-import__controls">
					<label v-if="hintDismissed">
						<span>Порог уверенности</span>
						<input v-model.number="matchThreshold" class="product-import__input el-inp" type="number" min="0" max="1" step="0.05">
					</label>
					<span v-else></span>
					<div class="product-import__buttons">
						<button class="el-btn product-import__btn-secondary" :disabled="isLoading" @click="importDryRun">
							{{ isLoading && isDryRun ? 'Проверяем...' : 'Только проверить' }}
						</button>
						<button class="el-btn" :disabled="isLoading" @click="importProduct">
							{{ isLoading && !isDryRun ? 'Импортируем...' : 'Импортировать' }}
						</button>
					</div>
				</div>
			</div>

			<div class="product-import__side">
				<div v-if="!hintDismissed" class="product-import__hint">
					<div class="product-import__hint-header">
						<div class="product-import__hint-title">Как пользоваться</div>
						<button class="product-import__dismiss" @click="hintDismissed = true" title="Скрыть">×</button>
					</div>
					<div>Вставь данные одного товара и нажми импорт. После создания ниже появятся ссылки на карточки Element.</div>
				</div>

				<div v-if="error" class="product-import__error">{{ error }}</div>

				<div v-if="links.length" class="product-import__links">
					<div class="product-import__hint-title">Открыть в Element</div>
					<a
						v-for="link in links"
						:key="link.url"
						:href="link.url"
						:class="{ 'product-import__link--new': link.isNew }"
						target="_blank"
					>{{ link.name }}</a>
				</div>
			</div>
		</div>

		<div v-if="normalizedText" ref="resultBlock" class="product-import__result">
			<div class="product-import__result-head">
				<div class="product-import__hint-title">Ответ GPT</div>
				<div class="product-import__result-actions">
					<span v-if="normalizedParseError" class="product-import__json-error">Невалидный JSON</span>
					<button
						v-if="lastNormalized && !links.length"
						class="el-btn product-import__secondary-button"
						:disabled="isLoading || !!normalizedParseError"
						@click="importNormalized"
					>
						Записать этот результат в БД
					</button>
				</div>
			</div>
			<textarea
				v-model="normalizedText"
				class="product-import__result-textarea"
				:rows="normalizedRows"
				spellcheck="false"
			></textarea>
		</div>

		<transition name="product-import__scrolltop-fade">
			<button
				v-if="showScrollTop"
				class="product-import__scrolltop"
				@click="scrollToTop"
				title="Наверх"
			>↑</button>
		</transition>
	</div>
</template>

<script>
	export default {
		props: ['url'],
		data() {
			return {
				rawProduct: '',
				allowNewDictionaries: true,
				matchThreshold: 0.9,
				isLoading: false,
				isDryRun: false,
				error: '',
				links: [],
				normalizedText: '',
				normalizedParseError: false,
				lastNormalized: null,
				hintDismissed: false,
				showScrollTop: false,
			}
		},
		computed: {
			normalizedRows() {
				return Math.max(20, (this.normalizedText.match(/\n/g) || []).length + 2);
			}
		},
		watch: {
			normalizedText(val) {
				try {
					this.lastNormalized = JSON.parse(val);
					this.normalizedParseError = false;
				} catch (e) {
					this.normalizedParseError = true;
				}
			}
		},
		mounted() {
			this._scrollEl = document.querySelector('.content-wrapper');
			this._onScroll = () => {
				this.showScrollTop = (this._scrollEl ? this._scrollEl.scrollTop : window.scrollY) > 300;
			};
			(this._scrollEl || window).addEventListener('scroll', this._onScroll);
		},
		beforeDestroy() {
			(this._scrollEl || window).removeEventListener('scroll', this._onScroll);
		},
		methods: {
			scrollToTop() {
				(this._scrollEl || window).scrollTo({ top: 0, behavior: 'smooth' });
			},
			async importProduct() {
				const product = this.parseProduct();
				if (!product) {
					this.error = 'Вставь данные товара';
					return;
				}
				await this.sendImport(product, false, false);
			},
			async importDryRun() {
				const product = this.parseProduct();
				if (!product) {
					this.error = 'Вставь данные товара';
					return;
				}
				await this.sendImport(product, false, true);
			},
			async importNormalized() {
				if (!this.lastNormalized)
					return;
				await this.sendImport(this.lastNormalized, true, false);
			},
			async sendImport(product, normalized, dryRun) {
				this.error = '';
				this.links = [];
				this.isDryRun = dryRun;
				if (!normalized) {
					this.normalizedText = '';
					this.lastNormalized = null;
					this.normalizedParseError = false;
				}

				this.isLoading = true;
				try {
					const result = await this.$axios.post('/ext/product_import_ext/import/run/', {
						product,
						options: {
							allow_new_dictionaries: this.allowNewDictionaries,
							dry_run: dryRun,
							match_threshold: this.matchThreshold,
							normalized
						}
					});

					if (!result.data || !result.data.success) {
						this.error = (result.data && result.data.message) ? result.data.message : 'Не удалось импортировать товар';
						return;
					}

					const data = result.data.data || {};
					this.lastNormalized = data.normalized || null;
					this.normalizedText = JSON.stringify(this.lastNormalized || {}, null, 2);
					this.normalizedParseError = false;
					this.links = this.buildLinks(data.import ? data.import.links : {});
					this.ElMessage(dryRun ? 'Проверка завершена' : 'Товар импортирован');

					await this.$nextTick();
					if (this.$refs.resultBlock && this._scrollEl) {
						const offset = this.$refs.resultBlock.getBoundingClientRect().top
							+ this._scrollEl.scrollTop
							- this._scrollEl.getBoundingClientRect().top
							- 20;
						this._scrollEl.scrollTo({ top: offset, behavior: 'smooth' });
					}
				} catch (error) {
					this.error = error.response && error.response.data && error.response.data.message
						? error.response.data.message
						: 'Ошибка запроса импорта';
				} finally {
					this.isLoading = false;
				}
			},
			parseProduct() {
				const value = this.rawProduct.trim();
				if (!value)
					return false;
				try {
					return JSON.parse(value);
				} catch (e) {
					return { text: value };
				}
			},
			buildLinks(links) {
				const result = [];
				if (links.product)
					result.push({ name: 'Товар', url: links.product });
				if (links.category)
					result.push({ name: 'Категория', url: links.category });
				if (links.brand)
					result.push({ name: 'Бренд', url: links.brand });
				(links.offers || []).forEach((url, index) => {
					result.push({ name: `Оффер ${index + 1}`, url });
				});
				(links.attribute_groups || []).forEach(item => {
					result.push({ name: `Группа: ${item.name}`, url: item.url, isNew: true });
				});
				(links.attribute_codes || []).forEach(item => {
					result.push({ name: `Код: ${item.name}`, url: item.url, isNew: true });
				});
				(links.variant_types || []).forEach(item => {
					result.push({ name: `Тип варианта: ${item.name}`, url: item.url, isNew: true });
				});
				return result;
			}
		}
	}
</script>

<style lang="scss">
	.product-import {
		max-width: 1180px;
		color: #19202a;
	}

	.product-import__head {
		margin-bottom: 24px;

		h1 {
			margin: 0 0 8px;
			font-size: 28px;
			font-weight: 700;
		}

		p {
			margin: 0;
			max-width: 760px;
			line-height: 1.5;
			color: rgba(25, 32, 42, 0.72);
		}
	}

	.product-import__grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 320px;
		gap: 24px;
		align-items: start;
	}

	.product-import__main,
	.product-import__side,
	.product-import__result {
		border: 1px solid rgba(103, 115, 135, 0.22);
		border-radius: 8px;
		background: #fff;
		padding: 18px;
	}

	.product-import__label,
	.product-import__controls span {
		display: block;
		margin-bottom: 8px;
		font-size: 13px;
		font-weight: 600;
	}

	.product-import__textarea {
		width: 100%;
		min-height: 280px;
		padding: 14px;
		resize: vertical;
		line-height: 1.45;
	}

	.product-import__options {
		display: grid;
		gap: 10px;
		margin: 16px 0;
	}

	.product-import__checkbox {
		display: flex;
		gap: 8px;
		align-items: center;
		font-size: 14px;
	}

	.product-import__controls {
		display: flex;
		gap: 14px;
		align-items: end;
		justify-content: space-between;
		margin-top: 16px;
	}

	.product-import__buttons {
		display: flex;
		gap: 8px;
		flex-shrink: 0;
	}

	.product-import__btn-secondary {
		background: transparent;
		color: #19202a;
		border: 1px solid rgba(103, 115, 135, 0.35);

		&:hover:not(:disabled) {
			background: rgba(103, 115, 135, 0.08);
		}
	}

	.product-import__input {
		width: 140px;
	}

	.product-import__hint,
	.product-import__error,
	.product-import__links {
		padding: 14px;
		border-radius: 8px;
		background: rgba(103, 115, 135, 0.08);
		line-height: 1.45;
	}

	.product-import__hint-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;

		.product-import__hint-title {
			margin-bottom: 0;
		}
	}

	.product-import__dismiss {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 22px;
		height: 22px;
		padding: 0;
		border: none;
		border-radius: 4px;
		background: transparent;
		color: rgba(25, 32, 42, 0.45);
		font-size: 18px;
		line-height: 1;
		cursor: pointer;
		flex-shrink: 0;

		&:hover {
			background: rgba(25, 32, 42, 0.08);
			color: #19202a;
		}
	}

	.product-import__hint-title {
		margin-bottom: 8px;
		font-size: 13px;
		font-weight: 700;
	}

	.product-import__error {
		margin-top: 14px;
		background: rgba(208, 18, 70, 0.1);
		color: #d01246;
	}

	.product-import__links {
		display: grid;
		gap: 8px;
		margin-top: 14px;

		a {
			color: #2c6bd6;
			text-decoration: none;
			font-size: 14px;

			&.product-import__link--new::after {
				content: 'новый';
				margin-left: 6px;
				padding: 1px 5px;
				border-radius: 3px;
				background: rgba(44, 107, 214, 0.12);
				color: #2c6bd6;
				font-size: 11px;
				font-weight: 600;
				vertical-align: middle;
			}
		}
	}

	.product-import__result {
		margin-top: 24px;

		.product-import__result-head {
			display: flex;
			gap: 12px;
			align-items: center;
			justify-content: space-between;
			margin-bottom: 10px;
		}

		.product-import__hint-title {
			margin-bottom: 0;
		}

		.product-import__result-actions {
			display: flex;
			align-items: center;
			gap: 12px;
		}

		.product-import__secondary-button {
			white-space: nowrap;
		}

		.product-import__json-error {
			font-size: 12px;
			color: #d01246;
		}
	}

	.product-import__result-textarea {
		width: 100%;
		padding: 14px;
		border: 1px solid rgba(103, 115, 135, 0.22);
		border-radius: 8px;
		background: rgba(25, 32, 42, 0.05);
		font-family: 'Courier New', Courier, monospace;
		font-size: 13px;
		line-height: 1.45;
		color: #19202a;
		resize: vertical;
		box-sizing: border-box;

		&:focus {
			outline: none;
			border-color: #2c6bd6;
			background: #fff;
		}
	}

	.product-import__scrolltop {
		position: fixed;
		right: 32px;
		bottom: 32px;
		width: 40px;
		height: 40px;
		border: none;
		border-radius: 50%;
		background: #19202a;
		color: #fff;
		font-size: 18px;
		line-height: 1;
		cursor: pointer;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
		z-index: 100;
		display: flex;
		align-items: center;
		justify-content: center;

		&:hover {
			background: #2c6bd6;
		}
	}

	.product-import__scrolltop-fade-enter-active,
	.product-import__scrolltop-fade-leave-active {
		transition: opacity 0.2s, transform 0.2s;
	}

	.product-import__scrolltop-fade-enter,
	.product-import__scrolltop-fade-leave-to {
		opacity: 0;
		transform: translateY(8px);
	}

	@media (max-width: 900px) {
		.product-import__grid {
			grid-template-columns: 1fr;
		}

		.product-import__controls {
			align-items: stretch;
			flex-direction: column;
		}

		.product-import__buttons {
			flex-direction: column;
		}
	}
</style>
